// fetch wrapper for /api/v1/* (plan 11.4.2): same-origin cookies (credentials: 'include'), CSRF double-submit
// (cookie aiax_csrf → header x-csrf-token on every state-changing request), the error envelope
// { error: { code, message, requestId, details? } } turned into ApiError, and 401 → back to /login (address kept).
import type { ZodType } from 'zod';
import { ErrorEnvelopeSchema } from './types';

export const CSRF_COOKIE = 'aiax_csrf';
export const CSRF_HEADER = 'x-csrf-token';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly details?: unknown;
  constructor(status: number, code: string, message: string, requestId?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}
export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const hit = document.cookie.split(';').map((c) => c.trim()).find((c) => c.startsWith(name + '='));
  if (!hit) return null;
  try {
    return decodeURIComponent(hit.slice(name.length + 1));
  } catch {
    return hit.slice(name.length + 1);
  }
}

type UnauthorizedHandler = (err: ApiError) => void;
let onUnauthorized: UnauthorizedHandler | null = null;
/** The app registers what a 401 does (clear cached data, go to /login?next=…). Returns the unregister function. */
export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): () => void {
  onUnauthorized = fn;
  return () => {
    if (onUnauthorized === fn) onUnauthorized = null;
  };
}

export type QueryValue = string | number | boolean | null | undefined;
export interface ApiRequest<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Readonly<Record<string, QueryValue | readonly QueryValue[]>>;
  signal?: AbortSignal;
  /** Validate (and transform) the JSON body. */
  schema?: ZodType<T>;
  /** A 401 is an expected answer (sign-in endpoints, "am I signed in?") — do not send the person to /login. */
  allow401?: boolean;
  headers?: Readonly<Record<string, string>>;
}

const SAFE = new Set(['GET', 'HEAD', 'OPTIONS']);

export function apiUrl(path: string, query?: ApiRequest<unknown>['query']): string {
  if (!path.startsWith('/api/')) throw new Error(`apiFetch: path must start with /api/ (got ${path})`);
  const qs = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      const list = Array.isArray(v) ? v : [v];
      for (const one of list) if (one !== undefined && one !== null && one !== '') qs.append(k, String(one));
    }
  }
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

async function readBody(res: Response): Promise<unknown> {
  if (res.status === 204 || res.status === 205) return undefined;
  const type = res.headers.get('content-type') || '';
  const text = await res.text();
  if (!text) return undefined;
  if (type.includes('json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}

/** Call an /api/v1 endpoint and return its JSON (or throw ApiError). */
export async function apiFetch<T = unknown>(path: string, req: ApiRequest<T> = {}): Promise<T> {
  const method = req.method ?? (req.body !== undefined ? 'POST' : 'GET');
  const headers: Record<string, string> = { Accept: 'application/json', ...req.headers };
  if (req.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!SAFE.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers[CSRF_HEADER] = csrf;
  }
  let res: Response;
  try {
    res = await fetch(apiUrl(path, req.query), {
      method,
      headers,
      credentials: 'include',
      body: req.body !== undefined ? JSON.stringify(req.body) : undefined,
      signal: req.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(0, 'NETWORK', e instanceof Error ? e.message : 'network error');
  }
  const body = await readBody(res);
  if (!res.ok) {
    const env = ErrorEnvelopeSchema.safeParse(body);
    const err = env.success
      ? new ApiError(res.status, env.data.error.code, env.data.error.message, env.data.error.requestId, env.data.error.details)
      : new ApiError(res.status, `HTTP_${res.status}`, res.statusText || `HTTP ${res.status}`, res.headers.get('x-request-id') ?? undefined);
    if (res.status === 401 && !req.allow401 && onUnauthorized) onUnauthorized(err);
    throw err;
  }
  if (!req.schema) return body as T;
  const parsed = req.schema.safeParse(body);
  if (!parsed.success) throw new ApiError(res.status, 'BAD_RESPONSE', `unexpected response from ${path}`, res.headers.get('x-request-id') ?? undefined, parsed.error.issues);
  return parsed.data;
}
